import { injectable } from '@servicetitan/react-ioc';
import { CancelToken } from 'axios';

import { HttpClient } from './http-client';
import { BaseRequestModel } from '../models/base-request.model';
import { IQuestionOptionModel } from '../../question-picker/models/questions.model';
import { MessageType } from '../models/message-type.model';
import { CalculatorBaseRequestDto } from '../../calculator/models/calculator.dto';


export interface PredefinedQuestion {
    role: 'user';
    id: IQuestionOptionModel['id'];
    content: string;
    timestamp: Date;
}

export interface QuestionResponseContent {
    entity_data: unknown;
    intro: string;
    fallback_text: string;
}


export interface FreeFormQuestionResponseModel {
    response: {
        role: 'assistant';
        content: QuestionResponseContent;
        entity_type: MessageType;
        timestamp: string;
    };
}

interface SendPredefinedQuestionRequestModel extends BaseRequestModel {
    job_id: number;
    question: PredefinedQuestion;
}

export interface PredefinedQuestionResponseModel {
    response: {
        role: 'assistant';
        content: QuestionResponseContent;
        entity_type: MessageType;
        timestamp: string;
    };
}

@injectable()
export class ChatApi {
    constructor(private readonly httpClient: HttpClient) {
    }

    sendFreeFormQuestion = async (
        data: SendFreeFormQuestionRequestModel,
        cancellationToken?: CancelToken,
    ) => {
        return this.httpClient.post<
            FreeFormQuestionResponseModel,
            SendFreeFormQuestionRequestModel
        >('chat', data, {
            cancelToken: cancellationToken,
        });
    };

    sendEquipmentQuestion = async (
        data: EquipmentQuestionRequestModel,
        cancellationToken?: CancelToken,
    ) => {
        return this.httpClient.post<
            EquipmentQuestionResponseModel,
            EquipmentQuestionRequestModel
        >('/equipment_chat', data, {
            cancelToken: cancellationToken,
        });
    };

    sendTroubleshootingQuestion = async (
        data: EquipmentQuestionRequestModel,
        cancellationToken?: CancelToken,
    ) => {
        return this.httpClient.post<
            EquipmentQuestionResponseModel,
            EquipmentQuestionRequestModel
        >('/troubleshooting_chat', data, {
            cancelToken: cancellationToken,
        });
    };

    sendPredefinedQuestion = async (
        data: SendPredefinedQuestionRequestModel,
        cancellationToken?: CancelToken,
    ) => {
        return this.httpClient.post<
            PredefinedQuestionResponseModel,
            SendPredefinedQuestionRequestModel
        >('/predefined_answer', data, {
            cancelToken: cancellationToken,
        });
    };

    // Generic agent chat call. `endpoint` comes from the agent registry so new agents
    // drop in without API-layer changes. The BFF injects tenant_id/technician_id/user_id;
    // the response reuses the existing free-form shape (entity_type maps to MessageType).
    sendAgentQuestion = async (
        endpoint: string,
        data: SendAgentQuestionRequestModel,
        cancellationToken?: CancelToken,
    ) => {
        return this.httpClient.post<FreeFormQuestionResponseModel, SendAgentQuestionRequestModel>(
            endpoint,
            data,
            {
                cancelToken: cancellationToken,
            }
        );
    };
}

export type AgentLaunchContext = 'mid-call' | 'recording' | 'general';

export interface SendAgentQuestionRequestModel extends BaseRequestModel {
    id: string;
    message: FreeFormQuestion;
    launch_context: AgentLaunchContext;
    job_id?: number;
    appointment_id?: number;
    recording_id?: string;
}

export type ChatType = 'chat' | "tool"

export interface EquipmentQuestionRequestModel extends BaseRequestModel {
    id: string;
    type?: ChatType;
    job_id: number;
    monolith_model_number?: string;
    monolith_equipment: MonolithEquipment | undefined;
    mapped_equipments: { id: string }[];
    question: string | CalculatorBaseRequestDto;
}

export interface MonolithEquipment {
    equipment_id: number;
    location_id: number;
}


export interface EquipmentQuestionResponseModel {
    answer: EquipmentChatAnswerApiModel;
}

export interface EquipmentChatAnswerApiModel {
    answer_text: string;
    citations: EquipmentChatAnswerCitationsApiModel[];
    has_found_answer?: boolean;
}

export interface EquipmentChatAnswerCitationsApiModel {
    document_id: string;
    page_number: number;
}

export interface SendFreeFormQuestionRequestModel extends BaseRequestModel {
    id: string;
    job_id: number;
    message: FreeFormQuestion;
}

export interface FreeFormQuestion {
    role: 'user';
    content: string;
    timestamp: Date;
}